import { BPButton } from '@/components/bp/bpbutton';
import { LabCaseRunPrintOptionsModel, LabClient, LabMarkerSettingsModel } from '@app/api2/api';
import { Formik } from 'formik';
import { chunk, flatten, groupBy, max } from 'lodash';
import * as React from 'react';
import { Button, Form, Modal, Table, TableCell, TableHeaderCell, TableBody, ModalHeader, ModalContent, TableHeader, TableRow, ModalActions } from '@/components/bp/bp-ui';

interface ILabMarkerSettingsDialogProps {
    labId: number;
    kitId: number;
    kitName: string;
    isOpen: boolean;
    onClose: () => void;
    onOpen: () => void;
    onOk: (values: Array<LabMarkerSettingsModel>) => void;
}

type MarkerDye = {
    key: string;
    color: string;
    detectionThreshold: any;
    dropInProbability: any;
    dropInHyperParam: any;
    femaleMu: any;
    maleMu: any;
    lsae: any;
    systems: LabMarkerSettingsModel[]
}


export const LabMarkerSettingsDialog: React.FunctionComponent<ILabMarkerSettingsDialogProps> = (props) => {
    const [markers, setMarkers] = React.useState<Array<MarkerDye>>([])
    // const [initValues, setInitValues] = React.useState(props.options)
    const client = new LabClient();
    const t = window.translation;

    React.useEffect(() => {
        async function fetch() {
            if (props.kitId && props.labId) {
                let res = await client.getKitMarkerSettings(props.labId, props.kitId);
                const systemsByColor = groupBy(res, x => x.color);
                const markerChunks = () => chunk(res, 5);

                const rows = Object.keys(systemsByColor).length > 1
                    ? Object.keys(systemsByColor).map((color, idx) => { return { key: `${color}_${idx}`, detectionThreshold: 0, dropInProbability: 0, dropInHyperParam: 0, color: color, femaleMu: 0, maleMu: 0, lsae: 1, systems: systemsByColor[color] }; })
                    : markerChunks().map((chunk, idx) => { return { key: `${Object.keys(systemsByColor)[0]}_${idx}`, detectionThreshold: 0, dropInProbability: 0, dropInHyperParam: 0, femaleMu: 0, maleMu: 0, lsae: 1, color: Object.keys(systemsByColor)[0], systems: chunk } })

                setMarkers(rows);
            } else {
                setMarkers([])
            }
        }

        fetch()
    }, [props.labId, props.kitId])

    const colNum = markers != null ? max(markers.map(x => x.systems.length)) : 0

    const getDropIns = async (value) => {
        try {
            let dropins = await client.calculateDropIn(props.labId, value);
            return dropins;
        } catch {
            return { dropInProbability: 0, lambda: 0 };
        }
    }

    const setMarkerRowDetectionThreshold = async (value, row: MarkerDye, markerNames: string[]) => {
        let dropins = await getDropIns(value);

        setMarkers(markers.map(x => {
            return {
                ...x,
                detectionThreshold: row.key == x.key ? value : x.detectionThreshold,
                dropInProbability: row.key == x.key ? dropins.dropInProbability : x.dropInProbability,
                dropInHyperParam: row.key == x.key ? dropins.lambda : x.dropInHyperParam,
                systems: x.systems.map(s => {
                    return {
                        ...s,
                        detectionThreshold: markerNames.indexOf(s.markerName) > -1 ? value : s.detectionThreshold,
                        dropInProbability: markerNames.indexOf(s.markerName) > -1 ? dropins.dropInProbability : s.dropInProbability,
                        dropInHyperParam: markerNames.indexOf(s.markerName) > -1 ? dropins.lambda : s.dropInHyperParam
                    }
                })
            }
        }))
    }

    const setMarkerRowDropInProbability = (value, row: MarkerDye, markerNames: string[]) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                dropInProbability: row.key == x.key ? value : x.dropInProbability,
                systems: x.systems.map(s => { return { ...s, dropInProbability: markerNames.indexOf(s.markerName) > -1 ? value : s.dropInProbability } })
            }
        }))
    }

    const setMarkerRowDropInHyperParam = (value, row: MarkerDye, markerNames: string[]) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                dropInHyperParam: row.key == x.key ? value : x.dropInHyperParam,
                systems: x.systems.map(s => { return { ...s, dropInHyperParam: markerNames.indexOf(s.markerName) > -1 ? value : s.dropInHyperParam } })
            }
        }))
    }

    const setFemaleMus = (value, row: MarkerDye, markerNames: string[]) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                femaleMu: row.key == x.key ? value : x.femaleMu,
                systems: x.systems.map(s => { return { ...s, femaleMu: markerNames.indexOf(s.markerName) > -1 ? value : s.femaleMu } })
            }
        }))
    }

    const setMaleMus = (value, row: MarkerDye, markerNames: string[]) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                maleMu: row.key == x.key ? value : x.maleMu,
                systems: x.systems.map(s => { return { ...s, maleMu: markerNames.indexOf(s.markerName) > -1 ? value : s.maleMu } })
            }
        }))
    }

    const setMaleMu = (value, markerName: string) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                systems: x.systems.map(s => { return { ...s, maleMu: markerName == s.markerName ? value : s.maleMu } })
            }
        }))
    }

    const setLSAEs = (value, row: MarkerDye, markerNames: string[]) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                lsae: row.key == x.key ? value : x.lsae,
                systems: x.systems.map(s => { return { ...s, lsae: markerNames.indexOf(s.markerName) > -1 ? value : s.lsae } })
            }
        }))
    }

    const setLSAE = (value, markerName: string) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                systems: x.systems.map(s => { return { ...s, lsae: markerName == s.markerName ? value : s.lsae } })
            }
        }))
    }

    const setFemaleMu = (value, markerName: string) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                systems: x.systems.map(s => { return { ...s, femaleMu: markerName == s.markerName ? value : s.femaleMu } })
            }
        }))
    }

    const setDetectionThreshold = async (value, markerName: string) => {
        let dropins = await getDropIns(value);

        setMarkers(markers.map(x => {
            return {
                ...x,
                systems: x.systems.map(s => {
                    return {
                        ...s,
                        detectionThreshold: markerName == s.markerName ? value : s.detectionThreshold,
                        dropInProbability: markerName == s.markerName ? dropins.dropInProbability : s.dropInProbability,
                        dropInHyperParam: markerName == s.markerName ? dropins.lambda : s.dropInHyperParam
                    }
                })
            }
        }))
    }

    const setDropInProbability = (value, markerName: string) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                systems: x.systems.map(s => { return { ...s, dropInProbability: markerName == s.markerName ? value : s.dropInProbability } })
            }
        }))
    }


    const setDropInHyperParam = (value, markerName: string) => {
        setMarkers(markers.map(x => {
            return {
                ...x,
                systems: x.systems.map(s => { return { ...s, dropInHyperParam: markerName == s.markerName ? value : s.dropInHyperParam } })
            }
        }))
    }


    return (
        <Formik
            initialValues={markers}
            onSubmit={values => {
                props.onOk(flatten(markers.map(x => x.systems)));
            }}>
            {fprops => {
                return <Modal as={Form} onSubmit={fprops.handleSubmit} open={props.isOpen} onClose={() => { props.onClose(); }} onOpen={() => { props.onOpen(); }}>
                    <ModalHeader>{props.kitName}</ModalHeader>
                    <ModalContent scrolling>
                        <div>
                            {markers.map((row, i) => {
                                const markers = row.systems
                                return (
                                    <Table fixed color={row.color as any} key={row.key} size={'small'} compact collapsing>
                                        <TableHeader fullWidth>
                                            <TableRow>
                                                <TableHeaderCell width={4} colSpan={2}>{row.color}</TableHeaderCell>
                                                {markers.map(marker => {
                                                    return <TableHeaderCell width={2} key={`h${marker.markerName}`}>{marker.markerName}</TableHeaderCell>
                                                })}
                                                {[...Array(colNum - markers.length).keys()].map((marker, idx) => {
                                                    return <TableHeaderCell width={2} key={`b${idx}`}></TableHeaderCell>
                                                })}
                                            </TableRow>
                                        </TableHeader>

                                        <TableBody>
                                            <TableRow>
                                                <TableCell>{t.parameter.names.detectionThreshold}</TableCell>
                                                <TableCell>
                                                    <input onChange={e => {
                                                        setMarkerRowDetectionThreshold(e.target.value, row, row.systems.map(x => x.markerName))
                                                    }} value={row.detectionThreshold}></input>
                                                </TableCell>

                                                {markers.map(marker => {
                                                    return <TableCell key={`c${marker.markerName}`}><input onChange={e => setDetectionThreshold(e.target.value, marker.markerName)} value={marker.detectionThreshold}></input></TableCell>
                                                })}
                                            </TableRow>

                                            <TableRow>
                                                <TableCell>{t.parameter.names.dropInProbability}</TableCell>

                                                <TableCell>
                                                    <input onChange={e => setMarkerRowDropInProbability(e.target.value, row, row.systems.map(x => x.markerName))} value={row.dropInProbability}></input>
                                                </TableCell>

                                                {markers.map(marker => {
                                                    return <TableCell key={`c${marker.markerName}`}><input onChange={e => setDropInProbability(e.target.value, marker.markerName)} value={marker.dropInProbability}></input></TableCell>
                                                })}
                                            </TableRow>

                                            <TableRow>
                                                <TableCell>{t.parameter.names.dropInHyperParam}</TableCell>

                                                <TableCell>
                                                    <input onChange={e => setMarkerRowDropInHyperParam(e.target.value, row, row.systems.map(x => x.markerName))} value={row.dropInHyperParam}></input>
                                                </TableCell>

                                                {markers.map(marker => {
                                                    return <TableCell key={`c${marker.markerName}`}><input onChange={e => setDropInHyperParam(e.target.value, marker.markerName)} value={marker.dropInHyperParam}></input></TableCell>
                                                })}
                                            </TableRow>

                                            <TableRow>
                                                <TableCell>{t.parameter.names.brutusFemaleMu}</TableCell>

                                                <TableCell>
                                                    <input onChange={e => setFemaleMus(e.target.value, row, row.systems.map(x => x.markerName))} value={row.femaleMu}></input>
                                                </TableCell>

                                                {markers.map(marker => {
                                                    return <TableCell key={`c${marker.markerName}`}><input onChange={e => setFemaleMu(e.target.value, marker.markerName)} value={marker.femaleMu}></input></TableCell>
                                                })}
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>{t.parameter.names.brutusMaleMu}</TableCell>

                                                <TableCell>
                                                    <input onChange={e => setMaleMus(e.target.value, row, row.systems.map(x => x.markerName))} value={row.maleMu}></input>
                                                </TableCell>

                                                {markers.map(marker => {
                                                    return <TableCell key={`c${marker.markerName}`}><input onChange={e => setMaleMu(e.target.value, marker.markerName)} value={marker.maleMu}></input></TableCell>
                                                })}
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>{"LSAE"}</TableCell>

                                                <TableCell>
                                                    <input onChange={e => setLSAEs(e.target.value, row, row.systems.map(x => x.markerName))} value={row.lsae}></input>
                                                </TableCell>

                                                {markers.map(marker => {
                                                    return <TableCell key={`c${marker.markerName}`}><input onChange={e => setLSAE(e.target.value, marker.markerName)} value={marker.lsae}></input></TableCell>
                                                })}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                )
                            })}
                        </div>
                    </ModalContent>
                    <ModalActions>
                        <BPButton type="submit">
                            {window.translation.common.save}
                        </BPButton>
                        <BPButton onClick={() => props.onClose()}>
                            {window.translation.common.cancel}
                        </BPButton>
                    </ModalActions>
                </Modal>
            }}
        </Formik>
    );
};

